import PropTypes from "prop-types"
import { useEffect, useMemo, useState, useCallback } from "react"
import { Card, Button, message, Select, InputNumber } from "antd"
import { ArrowRightOutlined, ShoppingCartOutlined } from "@ant-design/icons"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/authContext/index"
import "../assets/styles/ChatProductResults.css"

const ABSOLUTE_URL_REGEX = /^(?:https?:|data:|blob:)/i
const GCS_PUBLIC_URL_REGEX = /^https?:\/\/storage.googleapis.com\/[^/]+\/(.+)$/i
const GCS_MEDIA_URL_REGEX = /^https?:\/\/storage.googleapis.com\/download\/storage\/v1\/b\/[^/]+\/o\/([^?]+)(?:\?.*)?$/i
const GS_URL_REGEX = /^gs:\/\/[^/]+\/(.+)$/i
const BFF_BASE_URL = (process.env.REACT_APP_BFF_URL || "").replace(/\/$/, "")

const extractGcsPath = (value) => {
  if (typeof value !== "string") {
    return null
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }
  const gsMatch = trimmed.match(GS_URL_REGEX)
  if (gsMatch?.[1]) {
    return gsMatch[1]
  }
  const publicMatch = trimmed.match(GCS_PUBLIC_URL_REGEX)
  if (publicMatch?.[1]) {
    return publicMatch[1].split("?")[0]
  }
  const mediaMatch = trimmed.match(GCS_MEDIA_URL_REGEX)
  if (mediaMatch?.[1]) {
    try {
      return decodeURIComponent(mediaMatch[1])
    } catch {
      return mediaMatch[1]
    }
  }
  return null
}

const buildPublicImageUrl = (path) => {
  if (!path) {
    return path
  }
  if (ABSOLUTE_URL_REGEX.test(path)) {
    return path
  }
  const downloadPath = `/api/file-upload/download?path=${encodeURIComponent(path)}`
  return BFF_BASE_URL ? `${BFF_BASE_URL}${downloadPath}` : downloadPath
}

const normalizeImageValue = (value) => {
  if (typeof value !== "string") {
    return null
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }
  if (ABSOLUTE_URL_REGEX.test(trimmed)) {
    const gcsPath = extractGcsPath(trimmed)
    return gcsPath ? buildPublicImageUrl(gcsPath) : trimmed
  }
  return buildPublicImageUrl(trimmed)
}

const collectProductImages = (product) => {
  const images = []

  const push = (value) => {
    if (typeof value !== "string") {
      return
    }
    const trimmed = value.trim()
    if (trimmed && !images.includes(trimmed)) {
      images.push(trimmed)
    }
  }

  if (!product) {
    return images
  }

  push(product.imageUrl)
  push(product.image_url)
  push(product.image)
  push(product.thumbnail)
  push(product.thumbnailUrl)
  push(product.thumb)

  if (Array.isArray(product.images)) {
    product.images.forEach(push)
  }

  if (Array.isArray(product.thumbnails)) {
    product.thumbnails.forEach(push)
  }

  return images
}

const pickFirstString = (values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim()
    }
  }
  return null
}

const resolveNumericValue = (values) => {
  for (const candidate of values) {
    if (candidate === undefined || candidate === null || candidate === "") {
      continue
    }

    const parsed = Number(candidate)
    if (!Number.isNaN(parsed)) {
      return parsed
    }
  }
  return null
}

const resolvePrice = (product) => {
  const numeric = resolveNumericValue([
    product?.price,
    product?.precio,
    product?.cost,
    product?.amount,
    product?.importe,
  ])

  if (numeric !== null) {
    return {
      numeric,
      label: "$" + numeric.toFixed(2),
    }
  }

  const label = pickFirstString([
    product?.priceLabel,
    product?.price_text,
    product?.precio_formateado,
  ])

  return {
    numeric: null,
    label,
  }
}

const sanitizeNumericValue = (value) => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value
  }
  if (typeof value === 'string') {
    const normalized = value.replace(/[^0-9.,-]/g, '').replace(',', '.')
    if (!normalized) {
      return null
    }
    const parsed = Number(normalized)
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed
    }
  }
  return null
}

const normalisePriceEntries = (prices, fallbackPrice) => {
  if (Array.isArray(prices)) {
    const normalized = prices
      .map((price) => {
        const total = sanitizeNumericValue(
          price?.precioTotal ?? price?.precio ?? price?.total ?? price?.amount ?? price?.valor ?? price?.value,
        )
        if (!total) {
          return null
        }
        const rawLabel =
          price?.label ?? price?.Label ?? price?.cantidad ?? price?.quantity ?? price?.minQty ?? 1
        const labelInt = Number.parseInt(rawLabel, 10)
        const label = Number.isFinite(labelInt) && labelInt > 0 ? String(labelInt) : '1'
        return { label, precioTotal: total }
      })
      .filter(Boolean)

    if (normalized.length > 0) {
      return normalized
    }
  }

  const fallback = sanitizeNumericValue(fallbackPrice)
  if (fallback) {
    return [{ label: '1', precioTotal: fallback }]
  }
  return []
}

const normalizeQuantityValue = (value) => {
  const numeric = Number(value)
  if (Number.isFinite(numeric) && numeric > 0) {
    return Math.floor(numeric)
  }
  return 1
}

const extractNumericPriceFromProduct = (product) => {
  if (!product) {
    return null
  }

  const raw = product.raw || {}
  const directCandidates = [
    product.price,
    raw.price,
    raw.precio,
    raw.amount,
    raw.importe,
    raw.cost,
    product.priceLabel,
  ]

  for (const candidate of directCandidates) {
    const numeric = sanitizeNumericValue(candidate)
    if (numeric) {
      return numeric
    }
  }

  if (Array.isArray(raw.measures)) {
    for (const measure of raw.measures) {
      const prices = measure?.prices || measure?.precios
      if (Array.isArray(prices)) {
        for (const price of prices) {
          const numeric = sanitizeNumericValue(
            price?.precioTotal ?? price?.precio ?? price?.total ?? price?.amount ?? price?.valor ?? price?.value,
          )
          if (numeric) {
            return numeric
          }
        }
      }
    }
  }

  return null
}

const normalizeProduct = (product) => {
  const rawImages = collectProductImages(product)
  const images = []
  rawImages.forEach((value) => {
    const normalized = normalizeImageValue(value)
    if (normalized && !images.includes(normalized)) {
      images.push(normalized)
    }
  })

  const id =
    product?.id ??
    product?.productId ??
    product?.product_id ??
    product?.uid ??
    product?.slug ??
    product?.code ??
    null

  const name =
    product?.name ??
    product?.title ??
    product?.nombre ??
    product?.descripcion ??
    "Producto sin nombre"

  const price = resolvePrice(product)

  return {
    id,
    name,
    priceLabel: price.label,
    price: price.numeric,
    imageUrl: images[0] || null,
    images,
    measures: product?.measures,
    raw: product,
  }
}

const buildMeasureOptions = (product) => {
  const raw = product?.raw || {}
  const measures = Array.isArray(product?.measures)
    ? product.measures
    : Array.isArray(raw.measures)
    ? raw.measures
    : []
  const options = measures
    .map((measure, index) => {
      const label =
        typeof measure?.measure === 'string' && measure.measure.trim()
          ? measure.measure.trim()
          : typeof measure?.medida === 'string' && measure.medida.trim()
          ? measure.medida.trim()
          : `Presentacion ${index + 1}`

      return {
        value: label,
        label,
        prices: normalisePriceEntries(
          measure?.prices || measure?.precios,
          extractNumericPriceFromProduct(product),
        ),
      }
    })

  if (options.length > 0) {
    return options
  }

  const fallbackPrices = normalisePriceEntries(raw.prices || raw.precios, extractNumericPriceFromProduct(product))
  return [
    {
      value: 'General',
      label: 'General',
      prices: fallbackPrices,
    },
  ]
}

const ChatProductResults = ({ products, onSelectProduct }) => {
  const navigate = useNavigate()
  const { userLoggedIn } = useAuth()

  const normalizedProducts = useMemo(() => {
    if (!Array.isArray(products)) {
      return []
    }
    return products.map((item) => normalizeProduct(item))
  }, [products])

  const [displayProducts, setDisplayProducts] = useState(normalizedProducts)
  const [measureSelections, setMeasureSelections] = useState({})
  const [quantities, setQuantities] = useState({})

  const productKey = useCallback((product, index) => {
    const raw = product?.raw || {}
    return (
      product?.id ||
      raw.id ||
      raw.productId ||
      raw.product_id ||
      raw.uid ||
      raw.slug ||
      raw.code ||
      `prod-${index}`
    )
  }, [])

  useEffect(() => {
    setDisplayProducts(normalizedProducts)
  }, [normalizedProducts])

  useEffect(() => {
    setMeasureSelections((prev) => {
      const next = { ...prev }
      normalizedProducts.forEach((product, index) => {
        const key = productKey(product, index)
        if (next[key]) {
          return
        }
        const options = buildMeasureOptions(product)
        if (options.length > 0) {
          next[key] = options[0].value
        }
      })
      return next
    })

    setQuantities((prev) => {
      const next = { ...prev }
      normalizedProducts.forEach((product, index) => {
        const key = productKey(product, index)
        if (!next[key]) {
          next[key] = 1
        }
      })
      return next
    })
  }, [normalizedProducts, productKey])

  const handleImageError = useCallback(
    (index) => {
      setDisplayProducts((prev) =>
        prev.map((product, idx) => {
          if (idx !== index) {
            return product
          }
          return { ...product, imageUrl: null }
        }),
      )
    },
    [],
  )


const handleAddToCart = useCallback(
  (product, index) => {
    if (!userLoggedIn) {
      navigate('/signin')
      return
    }

    const raw = product.raw || {}
    const productId =
      product.id ||
      raw.id ||
      raw.productId ||
      raw.product_id ||
      raw.uid ||
      raw.slug ||
      raw.code

    if (!productId) {
      message.warning('No se pudo identificar el producto seleccionado.')
      return
    }

    const cart = JSON.parse(localStorage.getItem('cart') || '{}')

    const key = productKey(product, index)
    const options = buildMeasureOptions(product)
    const selectedMeasureName =
      measureSelections[key] || (options.length > 0 ? options[0].value : 'General')

    const selectedMeasure =
      options.find((option) => option.value === selectedMeasureName) || options[0]

    const quantityValue = quantities[key]
    const numericQuantity = normalizeQuantityValue(quantityValue)

    const pricesForMeasure = Array.isArray(selectedMeasure?.prices) ? selectedMeasure.prices : []

    if (!cart[productId]) {
      const imageList =
        Array.isArray(product.images) && product.images.length > 0
          ? product.images
          : Array.isArray(raw.images)
          ? raw.images
          : []
      cart[productId] = {
        name: product.name,
        images: imageList,
        measures: [],
      }
    }

    const measures = cart[productId].measures
    const measureName = selectedMeasure?.label || selectedMeasureName || 'General'
    const existingIndex = measures.findIndex((item) => item.measure === measureName)
    if (existingIndex !== -1) {
      measures[existingIndex].quantity += numericQuantity
    } else {
      measures.push({
        measure: measureName,
        quantity: numericQuantity,
        prices: pricesForMeasure,
      })
    }

    localStorage.setItem('cart', JSON.stringify(cart))
    window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { showDrawer: true } }))
    message.success(`${product.name} se agrego al carrito`)
  },
  [navigate, userLoggedIn, productKey, measureSelections, quantities],
)

  const handleOpenProduct = (product) => {
    if (onSelectProduct) {
      onSelectProduct(product.raw || product)
      return
    }

    if (product.id) {
      navigate(`/producto/${product.id}`)
    }
  }

  if (!Array.isArray(displayProducts) || displayProducts.length === 0) {
    return null
  }

  return (
    <div className="chat-product-results">
      <div className="chat-product-grid">
        {displayProducts.map((item, index) => {
          const normalized = item
          const key = productKey(normalized, index)
          const measureOptions = buildMeasureOptions(normalized)
          const selectedMeasure = measureSelections[key] || (measureOptions[0]?.value || 'General')
          const quantityValue = quantities[key] ?? 1

          return (
            <Card
              key={normalized.id || normalized.name}
              hoverable
              className="product-card chat-product-card"
              onClick={() => handleOpenProduct(normalized)}
              cover={
                <div className="chat-product-image-container">
                  {normalized.imageUrl ? (
                    <img
                      src={normalized.imageUrl}
                      alt={normalized.name}
                      className="chat-product-image"
                      onError={() => handleImageError(index)}
                    />
                  ) : (
                    <div className="chat-product-no-image">Sin imagen</div>
                  )}
                </div>
              }
            >
              <div className="product-details">
                <h4 className="product-title" title={normalized.name}>
                  {normalized.name}
                </h4>
                {normalized.priceLabel ? (
                  <div className="chat-product-price">{normalized.priceLabel}</div>
                ) : null}
              </div>
              <div className="chat-product-controls" onClick={(event) => event.stopPropagation()}>
                <Select
                  size="small"
                  value={selectedMeasure}
                  options={measureOptions.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  placeholder="Selecciona medida"
                  getPopupContainer={(trigger) => trigger.parentNode}
                  dropdownMatchSelectWidth={false}
                  onChange={(value) =>
                    setMeasureSelections((prev) => ({
                      ...prev,
                      [key]: value,
                    }))
                  }
                  disabled={measureOptions.length === 0}
                />
                <InputNumber
                  size="small"
                  min={1}
                  max={999999}
                  value={quantityValue}
                  onChange={(value) =>
                    setQuantities((prev) => ({
                      ...prev,
                      [key]: normalizeQuantityValue(value),
                    }))
                  }
                />
              </div>
              <div className="chat-product-actions">
                <Button
                  type="primary"
                  size="small"
                  icon={<ShoppingCartOutlined />}
                  onClick={(event) => {
                    event.stopPropagation()
                    handleAddToCart(normalized, index)
                  }}
                >
                  Agregar
                </Button>
                <Button
                  size="small"
                  className="chat-product-view"
                  onClick={(event) => {
                    event.stopPropagation()
                    handleOpenProduct(normalized)
                  }}
                  icon={<ArrowRightOutlined />}
                >
                  Ver detalle
                </Button>
              </div>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

ChatProductResults.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        imageUrl: PropTypes.string,
        images: PropTypes.arrayOf(PropTypes.string),
      }),
      PropTypes.object,
    ]),
  ),
  onSelectProduct: PropTypes.func,
}

ChatProductResults.defaultProps = {
  products: [],
  onSelectProduct: undefined,
}

export default ChatProductResults
